// src/pages/public/ProductPage.jsx
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ShoppingCart, Heart, Star, ChevronRight, Package, Shield, Truck, MessageCircle, ZoomIn, X, MapPin, Share2 } from 'lucide-react'
import { productsApi, wishlistApi, cartApi, personalizationApi, shippingApi } from '../../api/services'
import { useAuth } from '../../context/AuthContext'
import { useCart } from '../../context/CartContext'
import { Button, Badge, PageSpinner, Card, CardBody } from '../../components/common/ui'
import toast from 'react-hot-toast'

// ── Inline image zoom modal ───────────────────────────────────────────────────
function ImageZoomModal({ src, alt, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black/80 transition">
        <X size={22} />
      </button>
      <img src={src} alt={alt} className="max-w-full max-h-[90vh] object-contain rounded-xl shadow-2xl" onClick={e => e.stopPropagation()} />
    </div>
  )
}

// ── Per-variant stock badge ───────────────────────────────────────────────────
function StockBadge({ variant }) {
  if (!variant) return null
  const metres    = parseFloat(variant.stock_metres ?? 0)
  const threshold = parseFloat(variant.low_stock_threshold ?? 10)
  if (!variant.is_in_stock && !variant.allow_backorder)
    return <span className="text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">Out of stock</span>
  if (metres > 0 && metres <= threshold)
    return <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">Low stock</span>
  return <span className="text-[10px] font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded">In stock</span>
}

// ── Specification row ─────────────────────────────────────────────────────────
function SpecRow({ label, value, unit = '' }) {
  if (value === null || value === undefined || value === '') return null
  return (
    <div className="flex gap-3 text-sm">
      <dt className="w-36 text-muted flex-shrink-0">{label}</dt>
      <dd className="font-medium text-gray-800">{value}{unit}</dd>
    </div>
  )
}

export default function ProductPage() {
  const { slug }  = useParams()
  const { user }  = useAuth()
  const navigate  = useNavigate()
  const qc        = useQueryClient()

  const [qty, setQty]                         = useState(1)
  const [selectedVariant, setSelectedVariant] = useState(null)
  const [activeImage, setActiveImage]         = useState(0)
  const [activeTab, setActiveTab]             = useState('details')
  const [zoomSrc, setZoomSrc]                 = useState(null)

  const { data, isLoading } = useQuery({ queryKey: ['product', slug], queryFn: () => productsApi.show(slug) })
  const { data: reviewsData } = useQuery({ queryKey: ['product-reviews', slug], queryFn: () => productsApi.reviews(slug) })

  const addToCart = useMutation({
    mutationFn: () => cartApi.add({ product_id: product.id, variant_id: selectedVariant, quantity: qty }),
    onSuccess: () => { toast.success('Added to cart'); qc.invalidateQueries(['cart']) },
    onError:   (e) => toast.error(e.response?.data?.message || 'Failed to add'),
  })

  const toggleWishlist = useMutation({
    mutationFn: () => wishlistApi.toggle({ product_id: product.id }),
    onSuccess: (res) => toast.success(res.data?.data?.action === 'added' ? 'Added to wishlist ❤️' : 'Removed from wishlist'),
  })

  if (isLoading) return <PageSpinner />
  const product = data?.data?.data || data?.data
  if (!product) return <div className="py-24 text-center text-muted">Product not found.</div>

  const images   = product.images?.length ? product.images : [{ url: '/placeholder.jpg' }]
  const variants = product.variants || []
  const reviews  = reviewsData?.data?.data || []
  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + (r.overall_rating || r.rating || 0), 0) / reviews.length).toFixed(1)
    : null

  const tiers       = product.bulk_pricing_tiers || product.bulk_pricing || []
  const activeTier  = [...tiers].reverse().find(t => qty >= t.min_metres)
  const displayPrice = activeTier ? parseFloat(activeTier.price_per_metre) : parseFloat(product.retail_price || product.price || 0)
  const selectedVariantObj = variants.find(v => v.id === selectedVariant) ?? null
  const currentImageSrc = images[activeImage]?.url || images[activeImage]?.image_url || '/placeholder.jpg'

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs text-muted mb-6">
        <Link to="/" className="hover:text-pink">Home</Link><ChevronRight size={12} />
        <Link to="/fabrics" className="hover:text-pink">Fabrics</Link><ChevronRight size={12} />
        {product.category && <><Link to={`/fabrics?category=${product.category.slug}`} className="hover:text-pink">{product.category.name}</Link><ChevronRight size={12} /></>}
        <span className="text-gray-700 font-medium truncate max-w-xs">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Gallery */}
        <div>
          <div className="relative aspect-square rounded-2xl overflow-hidden bg-surface border border-border mb-3 group">
            <img src={currentImageSrc} alt={product.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
            <button
              onClick={() => setZoomSrc(currentImageSrc)}
              className="absolute top-3 right-3 bg-white/80 hover:bg-white rounded-full p-2 shadow transition opacity-0 group-hover:opacity-100"
              title="View full size"
            >
              <ZoomIn size={18} className="text-gray-700" />
            </button>
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {images.map((img, i) => (
                <button key={i} onClick={() => setActiveImage(i)}
                  className={`w-16 h-16 rounded-lg overflow-hidden border-2 flex-shrink-0 transition-colors ${activeImage === i ? 'border-pink' : 'border-border hover:border-gray-400'}`}>
                  <img src={img.url || img.image_url} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="space-y-5">
          <div>
            <div className="flex items-start justify-between gap-3">
              <h1 className="text-2xl font-display font-bold text-gray-900 leading-tight">{product.name}</h1>
              <button onClick={() => toggleWishlist.mutate()} className="p-2 rounded-full border border-border hover:border-pink transition-colors flex-shrink-0">
                <Heart size={18} className="text-muted" />
              </button>
            </div>
            {product.supplier && (
              <Link to={`/suppliers/${product.supplier.storefront_slug || product.supplier.id}`} className="text-sm text-pink hover:underline">
                by {product.supplier.business_name || product.supplier.name}
              </Link>
            )}
            {avgRating && (
              <div className="flex items-center gap-1.5 mt-1">
                <Star size={14} className="fill-yellow-400 text-yellow-400" />
                <span className="text-sm font-bold">{avgRating}</span>
                <span className="text-xs text-muted">({reviews.length} reviews)</span>
              </div>
            )}
          </div>

          {/* Price */}
          <div className="bg-gradient-to-r from-pink-light to-orange-50 rounded-2xl p-4">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-display font-bold text-gray-900">₹{displayPrice.toLocaleString('en-IN')}</span>
              <span className="text-sm text-muted">/metre</span>
              {product.mrp && parseFloat(product.mrp) > displayPrice && (
                <span className="text-sm text-muted line-through">₹{Number(product.mrp).toLocaleString('en-IN')}</span>
              )}
            </div>
            <p className="text-xs text-muted mt-1">
              +GST {product.gst_rate ?? 5}% &nbsp;·&nbsp; Min order: {product.min_order_retail ?? product.moq ?? 1}m
            </p>
          </div>

          {/* Bulk tiers */}
          {tiers.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wider">Bulk Pricing</p>
              <div className="flex gap-2 flex-wrap">
                {tiers.map((tier, i) => {
                  const active = qty >= tier.min_metres && (!tier.max_metres || qty <= tier.max_metres)
                  return (
                    <div key={i} className={`px-3 py-2 rounded-xl border text-xs ${active ? 'border-pink bg-pink-light text-pink' : 'border-border text-gray-600'}`}>
                      <span className="font-bold">₹{tier.price_per_metre}/m</span>
                      <span className="text-muted ml-1">{tier.min_metres}m{tier.max_metres ? `–${tier.max_metres}m` : '+'}</span>
                      {tier.saving_pct > 0 && <span className="ml-1 text-green-600 font-bold">{tier.saving_pct}% off</span>}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Colour / variant selector — with per-variant stock badges */}
          {variants.length > 0 && (
            <div>
              <p className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wider">Colour</p>
              <div className="flex flex-wrap gap-2">
                {variants.map(v => {
                  const outOfStock = !v.is_in_stock && !v.allow_backorder
                  return (
                    <button key={v.id}
                      onClick={() => !outOfStock && setSelectedVariant(v.id)}
                      disabled={outOfStock}
                      className={`px-3 py-1.5 rounded-full text-xs border flex flex-col items-start gap-0.5 transition-all ${
                        selectedVariant === v.id ? 'border-pink text-pink bg-pink-light'
                        : outOfStock ? 'border-border text-gray-400 opacity-50 cursor-not-allowed'
                        : 'border-border text-gray-700 hover:border-pink'
                      }`}
                    >
                      <span className="flex items-center gap-1.5">
                        {v.color_hex && <span className="w-3 h-3 rounded-full border border-white/50 shadow-sm" style={{ background: v.color_hex }} />}
                        {v.color_name}
                      </span>
                      <StockBadge variant={v} />
                    </button>
                  )
                })}
              </div>
              {selectedVariantObj && (
                <p className="text-xs text-muted mt-2">
                  {parseFloat(selectedVariantObj.stock_metres ?? 0) > 0
                    ? `${Number(selectedVariantObj.stock_metres).toLocaleString('en-IN')}m available`
                    : selectedVariantObj.allow_backorder ? 'Available on backorder' : 'Out of stock'}
                </p>
              )}
            </div>
          )}

          {/* Quantity */}
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">Metres</span>
            <div className="flex items-center border border-border rounded-full overflow-hidden">
              <button onClick={() => setQty(q => Math.max(product.min_order_retail ?? product.moq ?? 1, q - 1))} className="px-4 py-2 text-sm hover:bg-surface">−</button>
              <span className="px-4 py-2 text-sm font-bold w-16 text-center">{qty}</span>
              <button onClick={() => setQty(q => q + 1)} className="px-4 py-2 text-sm hover:bg-surface">+</button>
            </div>
            <span className="text-sm text-muted">= ₹{(qty * displayPrice).toLocaleString('en-IN')}</span>
          </div>

          {/* CTAs */}
          <div className="flex gap-3 flex-wrap">
            <Button className="flex-1" loading={addToCart.isPending}
              disabled={selectedVariantObj ? (!selectedVariantObj.is_in_stock && !selectedVariantObj.allow_backorder) : false}
              onClick={() => user ? addToCart.mutate() : navigate('/login')}>
              <ShoppingCart size={16} /> Add to Cart
            </Button>
            {product.is_sample_available && (
              <Link to={`/buyer/samples/request?product_id=${product.id}`}>
                <Button variant="secondary">
                  <Package size={16} /> Sample
                  {product.sample_price && <span className="ml-1 text-xs opacity-75">₹{Number(product.sample_price).toLocaleString('en-IN')}</span>}
                </Button>
              </Link>
            )}
            <Link to={`/messages/new?supplier_id=${product.supplier?.id}&product_id=${product.id}`}>
              <Button variant="secondary" title="Message supplier"><MessageCircle size={16} /></Button>
            </Link>
            <button
              onClick={() => {
                const url = window.location.href
                const text = `Check out ${product.name} on Fabrinza — ₹${displayPrice.toLocaleString('en-IN')}/m\n${url}`
                window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank')
              }}
              className="p-2.5 rounded-full border border-border hover:border-green-500 hover:bg-green-50 transition-colors"
              title="Share on WhatsApp"
            >
              <Share2 size={16} className="text-green-600" />
            </button>
          </div>

          {/* Trust badges */}
          <div className="flex items-center gap-4 py-3 border-t border-border text-xs text-muted">
            <span className="flex items-center gap-1"><Shield size={13} className="text-green-500" /> Verified Supplier</span>
            <span className="flex items-center gap-1"><Truck size={13} className="text-blue-500" /> Pan-India Shipping</span>
          </div>

          <div className="flex flex-wrap gap-2">
            {product.is_gi_tagged && <Badge variant="green">🏷️ GI Tagged{product.gi_tag_name ? ` — ${product.gi_tag_name}` : ''}</Badge>}
            {product.is_wholesale_only && <Badge variant="blue">Wholesale Only</Badge>}
            {product.is_sample_available && <Badge variant="grey">Sample Available ✓</Badge>}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-12">
        <div className="flex gap-1 border-b border-border mb-6">
          {['details', 'specs', 'care', 'reviews', 'supplier'].map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-5 py-3 text-sm font-bold capitalize border-b-2 -mb-px transition-colors ${activeTab === tab ? 'border-pink text-pink' : 'border-transparent text-muted hover:text-gray-700'}`}>
              {tab === 'reviews' && reviews.length ? `Reviews (${reviews.length})` : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Details */}
        {activeTab === 'details' && (
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-bold text-gray-900 mb-3">Description</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{product.description}</p>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 mb-3">Specifications</h3>
              <dl className="space-y-2">
                <SpecRow label="Fabric Type"  value={product.fabric_type} />
                <SpecRow label="Composition"  value={product.composition} />
                <SpecRow label="GSM"          value={product.gsm}          unit=" GSM" />
                <SpecRow label="Thread Count" value={product.thread_count} unit=" TPI" />
                <SpecRow label="Width"        value={product.width_inches} unit='"' />
                <SpecRow label="Weight/metre" value={product.weight_per_metre} unit=" g" />
                <SpecRow label="Weave"        value={product.weave_type} />
                <SpecRow label="Pattern"      value={product.pattern} />
                <SpecRow label="Finish"       value={product.finish} />
                <SpecRow label="Finish Type"  value={product.finish_type} />
                <SpecRow label="Origin"       value={[product.origin_city, product.origin_state].filter(Boolean).join(', ')} />
                <SpecRow label="HSN Code"     value={product.hsn_code} />
                <SpecRow label="SKU"          value={product.sku} />
              </dl>

              {/* Physical properties */}
              {(product.shrinkage_pct || product.pilling_grade || product.color_fastness || product.tensile_strength || product.drape_coefficient || product.moisture_absorption || product.opacity || product.stretch_pct) && (
                <>
                  <h4 className="font-bold text-gray-900 mt-5 mb-2 text-sm">Physical Properties</h4>
                  <dl className="space-y-2">
                    <SpecRow label="Shrinkage"     value={product.shrinkage_pct}       unit="%" />
                    <SpecRow label="Pilling Grade"  value={product.pilling_grade}       unit="/5" />
                    <SpecRow label="Color Fastness" value={product.color_fastness} />
                    <SpecRow label="Tensile Strength" value={product.tensile_strength} unit=" N/cm²" />
                    <SpecRow label="Drape"          value={product.drape_coefficient} />
                    <SpecRow label="Moisture Abs."  value={product.moisture_absorption} unit="%" />
                    <SpecRow label="Opacity"        value={product.opacity} />
                    <SpecRow label="Stretch"        value={product.stretch_pct}         unit="%" />
                    <SpecRow label="Stretch Dir."   value={product.stretch_direction} />
                  </dl>
                </>
              )}

              {/* Production */}
              {(product.dyeing_method || product.printing_method || product.loom_type || product.selvage_type) && (
                <>
                  <h4 className="font-bold text-gray-900 mt-5 mb-2 text-sm">Production & Craft</h4>
                  <dl className="space-y-2">
                    <SpecRow label="Dyeing Method"   value={product.dyeing_method} />
                    <SpecRow label="Printing Method"  value={product.printing_method} />
                    <SpecRow label="Loom Type"        value={product.loom_type} />
                    <SpecRow label="Selvage"          value={product.selvage_type} />
                  </dl>
                </>
              )}

              {/* Sustainability & usage */}
              {(product.certification || product.eco_friendly || product.recyclable || product.seasonal_suitability || product.recommended_use) && (
                <>
                  <h4 className="font-bold text-gray-900 mt-5 mb-2 text-sm">Sustainability & Usage</h4>
                  <dl className="space-y-2">
                    <SpecRow label="Certification"  value={product.certification} />
                    {product.eco_friendly && <SpecRow label="Eco-friendly" value="Yes" />}
                    {product.recyclable && <SpecRow label="Recyclable" value="Yes" />}
                    <SpecRow label="Season"         value={product.seasonal_suitability} />
                    <SpecRow label="Recommended Use" value={product.recommended_use} />
                  </dl>
                </>
              )}

              {/* Extensible specs */}
              {product.specifications?.length > 0 && (
                <>
                  <h4 className="font-bold text-gray-900 mt-5 mb-2 text-sm">Additional Specifications</h4>
                  <dl className="space-y-2">
                    {product.specifications.map(s => (
                      <SpecRow key={s.id} label={s.spec_key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} value={s.spec_value} unit={s.unit ? ` ${s.unit}` : ''} />
                    ))}
                  </dl>
                </>
              )}
            </div>
          </div>
        )}

        {/* Specs — full structured view */}
        {activeTab === 'specs' && (
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-bold text-gray-900 mb-3">Physical Properties</h3>
              <dl className="space-y-2">
                <SpecRow label="GSM"             value={product.gsm}               unit=" GSM" />
                <SpecRow label="Thread Count"    value={product.thread_count}      unit=" TPI" />
                <SpecRow label="Width"           value={product.width_inches}      unit='"' />
                <SpecRow label="Weight/metre"    value={product.weight_per_metre}  unit=" g" />
                <SpecRow label="Shrinkage"       value={product.shrinkage_pct}     unit="%" />
                <SpecRow label="Pilling Grade"   value={product.pilling_grade}     unit="/5" />
                <SpecRow label="Color Fastness"  value={product.color_fastness} />
                <SpecRow label="Tensile Strength" value={product.tensile_strength} unit=" N/cm²" />
                <SpecRow label="Drape"           value={product.drape_coefficient} />
                <SpecRow label="Moisture Abs."   value={product.moisture_absorption} unit="%" />
                <SpecRow label="Opacity"         value={product.opacity} />
                <SpecRow label="Stretch"         value={product.stretch_pct}       unit="%" />
                <SpecRow label="Stretch Dir."    value={product.stretch_direction} />
              </dl>
            </div>
            <div>
              <h3 className="font-bold text-gray-900 mb-3">Production & Craft</h3>
              <dl className="space-y-2">
                <SpecRow label="Weave Type"      value={product.weave_type} />
                <SpecRow label="Finish"          value={product.finish} />
                <SpecRow label="Finish Type"     value={product.finish_type} />
                <SpecRow label="Dyeing Method"   value={product.dyeing_method} />
                <SpecRow label="Printing Method" value={product.printing_method} />
                <SpecRow label="Loom Type"       value={product.loom_type} />
                <SpecRow label="Selvage"         value={product.selvage_type} />
              </dl>

              <h3 className="font-bold text-gray-900 mt-6 mb-3">Sustainability & Usage</h3>
              <dl className="space-y-2">
                <SpecRow label="Certification"   value={product.certification} />
                {product.eco_friendly && <SpecRow label="Eco-friendly" value="Yes" />}
                {product.recyclable && <SpecRow label="Recyclable" value="Yes" />}
                <SpecRow label="Season"          value={product.seasonal_suitability} />
                <SpecRow label="Recommended Use" value={product.recommended_use} />
              </dl>

              {product.specifications?.length > 0 && (
                <>
                  <h3 className="font-bold text-gray-900 mt-6 mb-3">Additional</h3>
                  <dl className="space-y-2">
                    {product.specifications.map(s => (
                      <SpecRow key={s.id} label={s.spec_key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} value={s.spec_value} unit={s.unit ? ` ${s.unit}` : ''} />
                    ))}
                  </dl>
                </>
              )}
            </div>
          </div>
        )}

        {/* Care */}
        {activeTab === 'care' && (
          <div className="max-w-xl">
            <h3 className="font-bold text-gray-900 mb-3">Care Instructions</h3>
            {product.care_instructions
              ? <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{product.care_instructions}</p>
              : <p className="text-sm text-muted">Care instructions not provided by the supplier.</p>
            }
          </div>
        )}

        {/* Reviews */}
        {activeTab === 'reviews' && (
          <div className="space-y-4">
            {reviews.length === 0
              ? <p className="text-sm text-muted py-8 text-center">No reviews yet.</p>
              : reviews.map(r => (
                <Card key={r.id}><CardBody>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex">
                      {[1,2,3,4,5].map(s => <Star key={s} size={12} className={s <= (r.overall_rating||r.rating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-200'} />)}
                    </div>
                    <span className="text-xs text-muted">{r.buyer?.name || 'Buyer'}</span>
                  </div>
                  {r.title && <p className="text-sm font-bold">{r.title}</p>}
                  {r.body  && <p className="text-sm text-gray-600 mt-1">{r.body}</p>}
                </CardBody></Card>
              ))
            }
          </div>
        )}

        {/* Supplier */}
        {activeTab === 'supplier' && product.supplier && (
          <div>
            <h3 className="font-bold text-gray-900">{product.supplier.business_name || product.supplier.name}</h3>
            {product.supplier.storefront?.tagline && <p className="text-sm text-muted mt-1">{product.supplier.storefront.tagline}</p>}
            <Link to={`/suppliers/${product.supplier.storefront_slug || product.supplier.id}`} className="mt-4 inline-block">
              <Button variant="secondary" size="sm">View Full Storefront →</Button>
            </Link>
          </div>
        )}
      </div>

      {/* ── Pincode Serviceability Check ──────────────────────────────────── */}
      <PincodeCheck />

      {/* ── Customers Also Bought ─────────────────────────────────────────── */}
      <AlsoBoughtSection productId={product.id} />

      {/* Zoom modal */}
      {zoomSrc && <ImageZoomModal src={zoomSrc} alt={product.name} onClose={() => setZoomSrc(null)} />}
    </div>
  )
}

// ── Pincode Serviceability Check ──────────────────────────────────────────────
function PincodeCheck() {
  const [pincode, setPincode] = useState('')
  const [result, setResult]   = useState(null)
  const [checking, setChecking] = useState(false)

  const check = async () => {
    if (!/^\d{6}$/.test(pincode)) { toast.error('Enter a valid 6-digit pincode'); return }
    setChecking(true)
    try {
      const res = await shippingApi.rates({ delivery_pincode: pincode })
      const data = res?.data?.data || res?.data
      setResult({ serviceable: true, providers: data?.providers || [], estimated_days: data?.estimated_days })
    } catch (e) {
      const msg = e.response?.data?.message
      setResult({ serviceable: false, message: msg || 'Delivery not available for this pincode' })
    } finally { setChecking(false) }
  }

  return (
    <div className="mt-8 max-w-md">
      <p className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wider flex items-center gap-1.5">
        <MapPin size={13} /> Check Delivery
      </p>
      <div className="flex gap-2">
        <input type="text" maxLength={6} value={pincode} onChange={e => { setPincode(e.target.value.replace(/\D/g, '')); setResult(null) }}
          placeholder="Enter 6-digit pincode" className="input flex-1 h-10 rounded-full text-sm" onKeyDown={e => e.key === 'Enter' && check()} />
        <Button variant="secondary" size="sm" onClick={check} loading={checking} className="rounded-full px-5">Check</Button>
      </div>
      {result && (
        <div className={`mt-2 text-xs px-3 py-2 rounded-lg ${result.serviceable ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
          {result.serviceable
            ? <>Delivery available{result.estimated_days ? ` — estimated ${result.estimated_days} days` : ''}</>
            : result.message}
        </div>
      )}
    </div>
  )
}

// ── Customers Also Bought ─────────────────────────────────────────────────────
function AlsoBoughtSection({ productId }) {
  const { addItem } = useCart()
  const { data } = useQuery({
    queryKey: ['recommendations', productId],
    queryFn: () => personalizationApi.recommendations({ product_id: productId }).catch(() => null),
    enabled: !!productId,
  })
  const products = data?.data?.data || data?.data || []
  if (!products.length) return null

  return (
    <section className="mt-14">
      <h2 className="font-display font-bold text-xl mb-5">Customers Also Bought</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {products.slice(0, 4).map(p => (
          <div key={p.id} className="group bg-white border border-border rounded-2xl overflow-hidden hover:shadow-card-md transition-all">
            <Link to={`/fabrics/${p.slug}`} className="block aspect-[4/3] bg-surface overflow-hidden">
              {p.primary_image
                ? <img src={p.primary_image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                : <div className="w-full h-full flex items-center justify-center text-4xl">🧵</div>}
            </Link>
            <div className="p-3">
              <Link to={`/fabrics/${p.slug}`} className="font-bold text-sm hover:text-pink transition-colors line-clamp-1">{p.name}</Link>
              <div className="flex items-center justify-between mt-2">
                <span className="font-extrabold text-sm">₹{Number(p.price || p.retail_price).toLocaleString('en-IN')}<span className="text-xs text-muted font-normal">/m</span></span>
                <button onClick={() => addItem(p.id, p.moq || 1).then(() => toast.success('Added!')).catch(() => toast.error('Login to add'))}
                  className="w-7 h-7 rounded-full bg-gradient-brand text-white flex items-center justify-center hover:shadow-brand-sm transition-all">
                  <ShoppingCart size={12} />
                </button>
              </div>
              {p.is_gi_tagged && <span className="inline-block mt-1.5 text-[10px] font-bold text-green-700 bg-green-50 px-1.5 py-0.5 rounded">GI Tagged</span>}
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
